/**
 * Plan — Phase 2 quote builder master container.
 *
 * Responsibilities:
 *   - Wrap children in <DraftProvider> so auto-save + local mirror work
 *   - Own the step (1..6) state and validation gates
 *   - Sticky header (close X + step indicator) + sticky bottom action bar
 *   - Side rail EstimateWidget on desktop, sticky compact one on mobile
 *   - Phase-3 placeholder dialog after Step 6 "Continue"
 *   - Telemetry: quote_started, quote_step_viewed, quote_step_completed,
 *     quote_draft_resumed, quote_submitted
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, X, Check, Loader2 } from "lucide-react";

import { DraftProvider, useDraft, clearLocalDraft } from "../lib/draft-store";
import {
  getPricingRules,
  getOccasionQuestionSet,
  startOtp,
  submitQuote,
  clearAuth as clearBuyerAuth,
} from "../lib/api";
import { track } from "../lib/track";
import { toast } from "../components/ui/sonner";

import StepIndicator from "../components/plan/StepIndicator";
import EstimateWidget from "../components/plan/EstimateWidget";
import CloseDialog from "../components/plan/CloseDialog";
import DraftSummary from "../components/plan/DraftSummary";
import OtpSheet from "../components/auth/OtpSheet";

import Step1Occasion, { step1IsValid } from "../components/plan/Step1Occasion";
import Step2DateLocation, { step2IsValid } from "../components/plan/Step2DateLocation";
import Step3Questions, { step3IsValid } from "../components/plan/Step3Questions";
import Step4Inspiration from "../components/plan/Step4Inspiration";
import Step5Budget, { step5IsValid } from "../components/plan/Step5Budget";
import Step6Contact, { step6IsValid } from "../components/plan/Step6Contact";

const TOTAL_STEPS = 6;

export default function Plan() {
  return (
    <DraftProvider>
      <PlanInner />
    </DraftProvider>
  );
}

function PlanInner() {
  const { id: draftId, payload, saving, lastSavedAt, flushNow, setField, mergePayload, reset } =
    useDraft();
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const [pricingRules, setPricingRules] = useState(null);
  const [questionSet, setQuestionSet] = useState(null);
  const [step, setStep] = useState(() => {
    const fromPayloadStep = Number(payload?.step) || 1;
    return Math.min(Math.max(fromPayloadStep, 1), TOTAL_STEPS);
  });
  const [closeOpen, setCloseOpen] = useState(false);
  const [otpOpen, setOtpOpen] = useState(false);
  const [devOtp, setDevOtp] = useState(null);
  const [showSummary, setShowSummary] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [estimateOpen, setEstimateOpen] = useState(false); // mobile

  const startedRef = useRef(false);

  // Load pricing rules once
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getPricingRules();
        if (!cancelled) setPricingRules(data);
      } catch {
        /* swallow — estimate widget will show empty state */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch occasion question set when occasion changes (for validation)
  useEffect(() => {
    const occ = payload?.occasion;
    if (!occ) {
      setQuestionSet(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await getOccasionQuestionSet(occ);
        if (!cancelled) setQuestionSet(data);
      } catch {
        if (!cancelled) setQuestionSet(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [payload?.occasion]);

  // quote_started on first paint
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    const resuming = params.get("resume") === "1";
    if (resuming) {
      track("quote_draft_resumed", { draftId });
      toast.success("Welcome back — picking up where you left off.");
    } else {
      track("quote_started", { properties: { entry: params.get("from") || "direct" } });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Pre-fill optional ?service=&package= context (one-shot)
  useEffect(() => {
    const svc = params.get("service");
    const pkg = params.get("package");
    if (!svc && !pkg) return;
    const ctx = payload?.context || {};
    if ((svc && ctx.service_slug !== svc) || (pkg && ctx.package_id !== pkg)) {
      mergePayload({
        context: { service_slug: svc || ctx.service_slug || null, package_id: pkg || ctx.package_id || null },
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Track quote_step_viewed + persist step into payload
  useEffect(() => {
    track("quote_step_viewed", { properties: { step }, draftId });
    setField("step", step);
    // scroll back to top
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  const requiredQIds = useMemo(() => {
    if (!questionSet?.questions) return [];
    return questionSet.questions
      .filter(
        (q) =>
          q.required &&
          !["location", "date", "city"].includes(q.type) &&
          !["location_count", "destination", "city", "event_date"].includes(q.id),
      )
      .map((q) => q.id);
  }, [questionSet]);

  const stepValid = useMemo(() => {
    switch (step) {
      case 1:
        return step1IsValid(payload);
      case 2:
        return step2IsValid(payload);
      case 3:
        return step3IsValid(payload, requiredQIds);
      case 4:
        return true; // optional
      case 5:
        return step5IsValid(payload);
      case 6:
        return step6IsValid(payload);
      default:
        return false;
    }
  }, [step, payload, requiredQIds]);

  const goNext = async () => {
    if (!stepValid) return;
    track("quote_step_completed", { properties: { step }, draftId });
    if (step < TOTAL_STEPS) {
      setStep((s) => s + 1);
      return;
    }
    // Step 6 → start OTP for buyer phone, then submit on verify.
    const contact = payload?.contact || {};
    const phone = contact.phone;
    const countryCode = contact.country_code || "+91";
    if (!phone) {
      toast.error("Add your phone number to continue.");
      return;
    }
    setSubmitting(true);
    try {
      await flushNow();
      const data = await startOtp({ phone, country_code: countryCode });
      setDevOtp(data?.dev_otp || null);
      setOtpOpen(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg =
        typeof detail === "string"
          ? detail
          : detail?.message || "Couldn't send verification code. Try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleOtpVerified = async () => {
    // OtpSheet has persisted tokens to localStorage. Now submit the quote.
    try {
      const quote = await submitQuote(draftId);
      track("quote_submitted", { draftId, properties: { quote_id: quote.id } });
      // CRITICAL: reset BEFORE navigation. `reset()` (a) flips the stopped
      // flag inside the draft store so any in-flight debounced save is
      // ignored, (b) cancels the save timer, (c) clears localStorage keys
      // (`shutterkart_draft_id`, `shutterkart_draft_payload`) AND
      // (d) clears in-memory state so the ResumeBar on Home doesn't fire.
      reset();
      setOtpOpen(false);
      navigate(`/quotes/${quote.id}/success`, { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const msg =
        typeof detail === "string"
          ? detail
          : detail?.message ||
            (status === 409
              ? "This request was already submitted."
              : "Couldn't submit the request. Please try again.");
      toast.error(msg);
      if (status === 401) {
        // Token went bad — sign out and prompt re-OTP
        clearBuyerAuth();
      }
    }
  };

  const goBack = () => {
    if (step === 1) {
      setCloseOpen(true);
      return;
    }
    setStep((s) => s - 1);
  };

  const onSaveExit = async () => {
    await flushNow();
    setCloseOpen(false);
    navigate("/");
  };

  const onDiscard = () => {
    reset();
    clearLocalDraft();
    setCloseOpen(false);
    navigate("/");
  };

  // Summary view (entered from Phase3Placeholder "View summary")
  if (showSummary) {
    return (
      <DraftSummary
        draftId={draftId}
        payload={payload}
        onBack={() => setShowSummary(false)}
      />
    );
  }

  const StepCmp = STEP_COMPONENTS[step - 1];

  return (
    <div
      data-testid="plan-page"
      className="relative mx-auto min-h-[calc(100vh-3.5rem)] w-full max-w-6xl px-4 pb-32 pt-6 sm:px-6 sm:pt-8 lg:pb-12"
    >
      {/* Top bar */}
      <header
        data-testid="plan-top-bar"
        className="sticky top-0 z-30 -mx-4 flex items-center justify-between gap-3 bg-[#FAF7F2]/95 px-4 py-3 backdrop-blur-md sm:-mx-6 sm:px-6"
      >
        <StepIndicator step={step} total={TOTAL_STEPS} />
        <div className="flex items-center gap-3">
          <span
            data-testid="plan-save-state"
            aria-live="polite"
            className="hidden text-[10px] uppercase tracking-[0.24em] text-[#1A1A1A]/45 sm:inline"
          >
            {saving ? (
              <span className="inline-flex items-center gap-1.5">
                <Loader2 className="h-3 w-3 animate-spin" /> Saving
              </span>
            ) : lastSavedAt ? (
              <span className="inline-flex items-center gap-1.5">
                <Check className="h-3 w-3 text-[#C8A24B]" /> Saved
              </span>
            ) : (
              "Draft"
            )}
          </span>
          <button
            type="button"
            data-testid="plan-close-button"
            aria-label="Close the quote builder"
            onClick={() => setCloseOpen(true)}
            className="rounded-full border border-[#1A1A1A]/15 p-2 text-[#1A1A1A]/70 transition hover:border-[#1A1A1A] hover:text-[#1A1A1A]"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </header>

      {/* Mobile estimate (compact) */}
      <div className="mt-4 lg:hidden">
        <button
          type="button"
          data-testid="estimate-mobile-toggle"
          onClick={() => setEstimateOpen((o) => !o)}
          className="w-full rounded-2xl border border-[#EAE3D2] bg-white p-4 text-left"
          aria-expanded={estimateOpen}
        >
          <MobileEstimateSummary
            payload={payload}
            pricingRules={pricingRules}
            open={estimateOpen}
          />
        </button>
        {estimateOpen ? (
          <div className="mt-3">
            <EstimateWidget payload={payload} pricingRules={pricingRules} compact />
          </div>
        ) : null}
      </div>

      {/* Body: 2-col on lg */}
      <div className="mt-6 grid grid-cols-1 gap-10 lg:grid-cols-[minmax(0,1fr)_360px] lg:gap-12">
        <main key={step} className="min-w-0 animate-fade-in">
          {StepCmp ? <StepCmp /> : null}
        </main>
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <EstimateWidget payload={payload} pricingRules={pricingRules} />
          </div>
        </aside>
      </div>

      {/* Sticky bottom action bar */}
      <footer
        data-testid="plan-action-bar"
        className="fixed inset-x-0 bottom-0 z-40 border-t border-[#EAE3D2] bg-[#FAF7F2]/95 backdrop-blur-md lg:static lg:mt-12 lg:border-0 lg:bg-transparent lg:px-0 lg:py-0 lg:backdrop-blur-none"
      >
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-3 sm:px-6">
          <button
            type="button"
            data-testid="plan-back-button"
            onClick={goBack}
            className="inline-flex items-center gap-2 rounded-full border border-[#1A1A1A]/15 px-5 py-2.5 text-xs uppercase tracking-[0.22em] text-[#1A1A1A]/80 transition hover:border-[#1A1A1A] hover:text-[#1A1A1A]"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
            {step === 1 ? "Exit" : "Back"}
          </button>

          <div className="text-center text-[10px] uppercase tracking-[0.22em] text-[#1A1A1A]/45 sm:text-xs">
            Step {step} / {TOTAL_STEPS}
          </div>

          <button
            type="button"
            data-testid="plan-next-button"
            onClick={goNext}
            disabled={!stepValid || submitting}
            className={[
              "inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-xs uppercase tracking-[0.22em] transition",
              stepValid && !submitting
                ? "bg-[#1A1A1A] text-[#FAF7F2] hover:bg-[#2a2a2a]"
                : "cursor-not-allowed bg-[#1A1A1A]/25 text-[#FAF7F2]",
            ].join(" ")}
          >
            {submitting ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : null}
            {step === TOTAL_STEPS ? "Continue to verify" : "Continue"}
            {!submitting ? <ChevronRight className="h-3.5 w-3.5" /> : null}
          </button>
        </div>
      </footer>

      <CloseDialog
        open={closeOpen}
        onOpenChange={setCloseOpen}
        onSaveExit={onSaveExit}
        onDiscard={onDiscard}
      />

      <OtpSheet
        open={otpOpen}
        onOpenChange={setOtpOpen}
        phone={payload?.contact?.phone || ""}
        countryCode={payload?.contact?.country_code || "+91"}
        devOtp={devOtp}
        resendStart={() =>
          startOtp({
            phone: payload?.contact?.phone,
            country_code: payload?.contact?.country_code || "+91",
          })
        }
        onEditPhone={() => {
          setOtpOpen(false);
          // Step 6 is already open; focus the phone input if available
          setTimeout(() => {
            try {
              document.querySelector('[data-testid="contact-phone"]')?.focus();
            } catch {
              /* ignore */
            }
          }, 200);
        }}
        onVerified={handleOtpVerified}
        submitLabel="Verify & submit"
      />
    </div>
  );
}

const STEP_COMPONENTS = [
  Step1Occasion,
  Step2DateLocation,
  Step3Questions,
  Step4Inspiration,
  Step5Budget,
  Step6Contact,
];

function MobileEstimateSummary({ payload, pricingRules, open }) {
  // Tiny inline summary — full widget renders below when expanded.
  const occ = payload?.occasion;
  return (
    <div className="flex items-center justify-between gap-3">
      <div>
        <p className="text-[10px] uppercase tracking-[0.28em] text-[#C8A24B]">
          Estimated range
        </p>
        <p className="mt-1 font-display text-lg text-[#1A1A1A]">
          {occ && pricingRules ? "Tap to view" : "Add details to see"}
        </p>
      </div>
      <span className="rounded-full border border-[#1A1A1A]/15 px-3 py-1 text-[10px] uppercase tracking-[0.22em] text-[#1A1A1A]/65">
        {open ? "Hide" : "Show"}
      </span>
    </div>
  );
}
